import { useEffect, useMemo, useRef, useState } from "react";
import { benchmarkFactorsService } from "../services/benchmarkFactorsService";
import {
  discoverFactorsService,
  type DiscoverFactorProductType,
  type DiscoverFactorsBatchRequest,
} from "../services/discoverFactorsService";
import {
  getSignedDriftedWeight,
  getSignedWeight,
  type StrategyEntry,
} from "../services/investorStrategyService";
import type { DraftFactorProductInput } from "../utils/strategyAnalyzerDraftFactors";
import {
  computeMoveContextForDays,
  currentUtcDayKey,
  reconstructDailyActiveSeries,
  type MoveContext,
  type ReconstructedDay,
} from "../utils/strategyMoveContext";
import type { ProductLookupEntry, ProductLookupKey } from "../utils/strategyUtils";

const FACTORS_PAGE_SIZE = 10000;

export interface DateBounds {
  min: string; // earliest reconstructed day "YYYY-MM-DD"
  max: string; // today
}

export interface UseStrategyMoveContextParams {
  enabled: boolean;
  strategies: StrategyEntry[];
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  benchmarkConId: number;
  isLoadingStrategies: boolean;
  isLoadingProducts: boolean;
  /** Focus day "YYYY-MM-DD"; null/undefined = today. Changing this does not refetch. */
  selectedDate?: string | null;
}

export interface UseStrategyMoveContextResult {
  moveContext: MoveContext | null;
  isLoading: boolean;
  error: string | null;
  dateBounds: DateBounds | null;
}

/**
 * Builds the benchmark-relative move context for the current strategy: fetches
 * each constituent's full factor history (admin route -- admin-only page) plus the
 * selected benchmark, reconstructs the current strategy as a backtest (rebalanced
 * at current signed weights), and pairs each day with the benchmark.
 *
 * Fetch is keyed to the selected user + benchmark and guarded with a monotonic
 * request token (CLAUDE.md rule 6). The focus DATE only re-derives the context
 * from the already-fetched series -- it never triggers a network request.
 */
export function useStrategyMoveContext({
  enabled,
  strategies,
  productLookup,
  benchmarkConId,
  isLoadingStrategies,
  isLoadingProducts,
  selectedDate,
}: UseStrategyMoveContextParams): UseStrategyMoveContextResult {
  const [days, setDays] = useState<ReconstructedDay[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;

    // Wait for both strategies AND the product catalog: until the lookup is loaded
    // a constituent's providerId is unknown, and computing now would reconstruct
    // from only the resolved legs (a different, partial strategy).
    if (!enabled || isLoadingStrategies || isLoadingProducts || strategies.length === 0) {
      setDays(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const weightByKey = new Map<string, number>();
    const requests: DiscoverFactorsBatchRequest[] = [];
    let hasUnresolvedLeg = false;
    for (const strategy of strategies) {
      // Current actual exposure (drifted weight), populated for anything > 30 min
      // old; fall back to the target weight for brand-new positions.
      const weight = getSignedDriftedWeight(strategy) ?? getSignedWeight(strategy);
      if (weight === 0) continue; // zero-weight legs do not affect the backtest
      const lookupKey = `${strategy.entityType}-${strategy.productId}` as ProductLookupKey;
      const lookup = productLookup.get(lookupKey);
      if (!lookup) {
        // A nonzero leg we cannot resolve means any reconstruction would describe a
        // partial strategy. Bail rather than show a sigma for the wrong portfolio.
        hasUnresolvedLeg = true;
        break;
      }
      const productType = strategy.entityType.toLowerCase() as DiscoverFactorProductType;
      weightByKey.set(`${productType}-${strategy.productId}`, weight);
      requests.push({
        providerId: lookup.providerId,
        productType,
        productId: strategy.productId,
        pageSize: FACTORS_PAGE_SIZE,
      });
    }

    if (hasUnresolvedLeg) {
      setDays(null);
      setError("Benchmark context unavailable for this strategy.");
      setIsLoading(false);
      return;
    }

    if (requests.length === 0) {
      setDays(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    Promise.all([
      // Admin-only page: use the admin factors route so products that require it
      // resolve correctly. (Benchmark factors are global market data.)
      discoverFactorsService.getFactorsBatch(requests, undefined, undefined, true),
      benchmarkFactorsService.getContractFactors(benchmarkConId),
    ])
      .then(([productResults, benchmarkFactors]) => {
        if (cancelled || fetchId !== fetchIdRef.current) return;
        const products: DraftFactorProductInput[] = productResults.map((result) => ({
          key: `${result.productType}-${result.productId}`,
          weight: weightByKey.get(`${result.productType}-${result.productId}`) ?? 0,
          factors: result.factors,
        }));
        setDays(reconstructDailyActiveSeries(products, benchmarkFactors));
      })
      .catch(() => {
        if (cancelled || fetchId !== fetchIdRef.current) return;
        setError("Failed to load benchmark context.");
        setDays(null);
      })
      .finally(() => {
        if (cancelled || fetchId !== fetchIdRef.current) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, strategies, productLookup, benchmarkConId, isLoadingStrategies, isLoadingProducts]);

  // Date change only re-derives from the already-fetched series -- no refetch.
  const moveContext = useMemo(
    () => (days ? computeMoveContextForDays(days, selectedDate ?? undefined) : null),
    [days, selectedDate]
  );

  const dateBounds = useMemo<DateBounds | null>(
    () => (days && days.length > 0 ? { min: days[0].date, max: currentUtcDayKey() } : null),
    [days]
  );

  return { moveContext, isLoading, error, dateBounds };
}
